import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getStore } from "../../storeRef";

export function createEntityService({ name, apiFetchFn }) {
  const fetchEntity = createAsyncThunk(`${name}/fetch`, async (id) => ({
    id: String(id),
    data: await apiFetchFn(id),
  }));

  const slice = createSlice({
    name,
    initialState: { byId: {} },
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(fetchEntity.pending, (state, action) => {
          const key = String(action.meta.arg);
          state.byId[key] = {
            ...(state.byId[key] ?? {}),
            loading: true,
            error: null,
          };
        })
        .addCase(fetchEntity.fulfilled, (state, action) => {
          const { id, data } = action.payload;
          state.byId[id] = { data, loading: false, error: null };
        })
        .addCase(fetchEntity.rejected, (state, action) => {
          const key = String(action.meta.arg);
          state.byId[key] = {
            ...(state.byId[key] ?? {}),
            loading: false,
            error: action.error?.message ?? "Failed to load",
          };
        });
    },
  });

  const inFlight = new Map();

  const getData = async (id) => {
    if (id === null || id === undefined) return undefined;
    const key = String(id);

    const pending = inFlight.get(key);
    if (pending) return pending;

    const request = getStore()
      .dispatch(fetchEntity(id))
      .unwrap()
      .then((result) => result.data)
      .finally(() => {
        if (inFlight.get(key) === request) inFlight.delete(key);
      });

    inFlight.set(key, request);
    return request;
  };

  const useEntityData = (id) => {
    const key = id === null || id === undefined ? null : String(id);
    const entry = useSelector((state) =>
      key === null ? undefined : state[name].byId[key],
    );

    useEffect(() => {
      if (key === null) return;
      getData(key).catch(() => {});
    }, [key]);

    const refetch = useCallback(() => {
      if (key === null) return Promise.resolve(undefined);
      return getData(key).catch(() => undefined);
    }, [key]);

    return {
      data: entry?.data,
      loading: entry ? Boolean(entry.loading) : key !== null,
      error: entry?.error ?? null,
      refetch,
    };
  };

  return { reducer: slice.reducer, getData, useEntityData };
}
